<script setup lang="ts">
import { clearIdeoDragSession, ideoDragSession } from './session';

defineOptions({
    name: 'IdeoDropZone',
    inheritAttrs: false
});

type AcceptRule = string | string[] | ((data: any, type: string) => boolean);

const props = withDefaults(defineProps<{
    accept?: AcceptRule | null;
    tag?: string;
    disabled?: boolean;
    acceptFiles?: boolean;
    label?: string;
    unstyled?: boolean;
    nested?: boolean;
}>(), {
    accept: null,
    tag: 'div',
    disabled: false,
    acceptFiles: false,
    label: 'Obszar upuszczania',
    unstyled: false,
    nested: false
});

const emit = defineEmits<{
    (e: 'drag-enter', data: any, event: DragEvent): void;
    (e: 'drag-leave', data: any, event: DragEvent): void;
    (e: 'drag-over', data: any, event: DragEvent): void;
    (e: 'drop', payload: { data: any; type: string; effect: string; files: File[] }, event: DragEvent | KeyboardEvent): void;
    (e: 'reject', data: any, event: DragEvent | KeyboardEvent): void;
}>();

const attrs = useAttrs();
const depth = ref(0);
const over = computed(() => depth.value > 0);
const hasFiles = ref(false);

function accepts(data = ideoDragSession.data, type = ideoDragSession.type)
{
    if (props.disabled)
        return false;

    if (hasFiles.value)
        return props.acceptFiles;

    if (!props.accept)
        return ideoDragSession.active;

    if (typeof props.accept === 'function')
        return props.accept(data, type);

    return Array.isArray(props.accept) ? props.accept.includes(type) : props.accept === type;
}

function onDragEnter(event: DragEvent)
{
    hasFiles.value = (event.dataTransfer?.files.length ?? 0) > 0 || event.dataTransfer?.types.includes('Files') === true;

    if (!ideoDragSession.active && !hasFiles.value)
        return;

    if (props.nested)
    {
        if (!accepts())
            return;
        event.stopPropagation();
    }
    event.preventDefault();
    depth.value++;
    emit('drag-enter', ideoDragSession.data, event);
}

function onDragLeave(event: DragEvent)
{
    if (props.nested && depth.value > 0)
        event.stopPropagation();
    depth.value = Math.max(0, depth.value - 1);

    if (depth.value === 0)
    {
        emit('drag-leave', ideoDragSession.data, event);
        hasFiles.value = false;
    }
}

function onDragOver(event: DragEvent)
{
    if (!ideoDragSession.active && !hasFiles.value)
        return;

    if (props.nested)
    {
        if (!accepts())
            return;
        event.stopPropagation();
    }
    event.preventDefault();

    if (event.dataTransfer)
        event.dataTransfer.dropEffect = accepts() ? ideoDragSession.effect : 'none';

    emit('drag-over', ideoDragSession.data, event);
}

function completeDrop(event: DragEvent | KeyboardEvent)
{
    const accepted = accepts();
    const files = event instanceof DragEvent ? Array.from(event.dataTransfer?.files ?? []) : [];
    const payload = {
        data: ideoDragSession.data,
        type: ideoDragSession.type,
        effect: ideoDragSession.effect,
        files
    };

    depth.value = 0;
    hasFiles.value = false;

    if (accepted)
        emit('drop', payload, event);
    else
        emit('reject', ideoDragSession.data, event);

    clearIdeoDragSession();
}

function onDrop(event: DragEvent)
{
    hasFiles.value = (event.dataTransfer?.files.length ?? 0) > 0 || event.dataTransfer?.types.includes('Files') === true;
    if (props.nested)
    {
        if (!accepts())
            return;
        event.stopPropagation();
    }
    event.preventDefault();
    completeDrop(event);
}

function onKeydown(event: KeyboardEvent)
{
    if (!ideoDragSession.active || !ideoDragSession.keyboard)
        return;

    if (event.key === 'Enter' || event.key === ' ')
    {
        if (props.nested)
        {
            if (!accepts())
                return;
            event.stopPropagation();
        }
        event.preventDefault();
        completeDrop(event);
    }
    else if (event.key === 'Escape')
    {
        event.preventDefault();
        clearIdeoDragSession();
    }
}

watch(() => ideoDragSession.active, active =>
{
    if (!active && props.nested)
    {
        depth.value = 0;
        hasFiles.value = false;
    }
});

onBeforeUnmount(() =>
{
    depth.value = 0;
    hasFiles.value = false;
});
</script>

<template>
    <component
        :is="props.tag"
        v-bind="attrs"
        :class="[
            attrs.class,
            props.unstyled ? '' : 'relative rounded-[8px] border border-dashed border-border-strong bg-transparent [transition:border-color_0.14s_ease,box-shadow_0.14s_ease,background_0.14s_ease] focus-visible:outline-[2px] focus-visible:outline-offset-[2px] focus-visible:outline-accent motion-reduce:transition-none dark:border-border-strong-dark',
            !props.unstyled && over && accepts() ? 'border-accent bg-[color-mix(in_oklch,var(--color-accent)_4%,transparent)] shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-accent)_18%,transparent)]' : '',
            !props.unstyled && over && !accepts() ? 'border-danger bg-danger-soft shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-danger)_15%,transparent)] dark:bg-danger-soft-dark' : '',
            props.disabled && !props.unstyled ? 'cursor-not-allowed opacity-[0.55]' : ''
        ]"
        :tabindex="ideoDragSession.active && ideoDragSession.keyboard && !props.disabled ? 0 : undefined"
        :aria-label="props.label"
        :aria-disabled="props.disabled ? 'true' : undefined"
        :data-over="over ? 'true' : undefined"
        :data-accepted="over ? String(accepts()) : undefined"
        :data-disabled="props.disabled ? 'true' : undefined"
        data-ideo-drop-zone
        @dragenter="onDragEnter"
        @dragleave="onDragLeave"
        @dragover="onDragOver"
        @drop="onDrop"
        @keydown="onKeydown"
    >
        <slot :over="over" :accepted="accepts()" :session="ideoDragSession" />
    </component>
</template>
